import { useEffect, useState } from 'react';
import { BottomMenu } from '../components/BottomMenu/BottomMenu';
import styles from './Card.module.css';
import { ApiService } from '../services/api.service';
import {
  BookingResponseDTO,
  ServiceCategoryResponseDTO,
  ServiceResponseDTO
} from '../components/types/ZaprosDTO';

// ======================= ТИПЫ ДАННЫХ =======================
export type Booking = BookingResponseDTO & {
  category?: ServiceCategoryResponseDTO;
  service?: ServiceResponseDTO;
  totalPrice?: number;
};

type Props = {
  onConfirm: () => void;
  onCancel: (id: number) => void;
};

// ======================= ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ =======================
const calculateDurationInHours = (start?: string, end?: string) => {
  if (!start || !end) return 0;

  try {
    const startTime = new Date(start).getTime();
    const endTime = new Date(end).getTime();
    return Math.ceil((endTime - startTime) / (1000 * 60 * 60));
  } catch {
    return 0;
  }
};

// ======================= ОСНОВНОЙ КОМПОНЕНТ =======================
export const Card = ({ onCancel }: Props) => {
  // ======================= СОСТОЯНИЯ КОМПОНЕНТА =======================
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [payUrl, setPayUrl] = useState('');
  const [adminUrl, setAdminUrl] = useState('');


  useEffect(() => {
    const fetchPayUrl = async () => {
      try {
        const url = await ApiService.getPayUrl();
        const admin = await ApiService.getAdminUrl();
        setPayUrl(url);
        setAdminUrl(admin);
      } catch (error) {
        console.error('Ошибка при получении URL оплаты:', error);
      }
    };
    fetchPayUrl();
  }, []);

  // ======================= ЗАГРУЗКА ДАННЫХ =======================
  const loadBookings = async () => {
    try {
      const bookingsData = await ApiService.getBookings();

      const enrichedBookings = await Promise.all(
        bookingsData.map(async (booking: BookingResponseDTO) => {
          try {
            const service = (await ApiService.getServices(booking.service_id))[0];
            const categories = await ApiService.getCategories();
            const category = categories.find(c => c.id === service.category_id);

            return {
              ...booking,
              service,
              category,
              totalPrice: booking.total_price || 0
            };
          } catch (e) {
            console.error('Ошибка загрузки данных:', e);
            return booking;
          }
        })
      );

      setBookings(enrichedBookings);
      setError(null);
    } catch (error) {
      console.error('Ошибка загрузки бронирований:', error);
      setError('Не удалось загрузить данные бронирований');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  // ======================= ФИЛЬТРАЦИЯ БРОНИРОВАНИЙ =======================
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  const currentBookings = bookings.filter(booking => {
    const bookingDate = new Date(booking.booking_date);
    bookingDate.setHours(0, 0, 0, 0);
    return bookingDate >= currentDate;
  });

  const historyBookings = bookings.filter(booking => {
    const bookingDate = new Date(booking.booking_date);
    bookingDate.setHours(0, 0, 0, 0);
    return booking.is_confirmed && bookingDate < currentDate;
  });

  // ======================= РАСЧЕТ СУММ ДЛЯ ОТОБРАЖЕНИЯ =======================
  const totalPendingSum = currentBookings
    .filter(b => !b.is_confirmed)
    .reduce((sum, booking) => sum + Number(booking.total_price || 0), 0);

  // ======================= ОБРАБОТКА ОТМЕНЫ БРОНИРОВАНИЯ =======================
  const handleCancel = async (bookingId: number) => {
    try {
      await ApiService.deleteBooking(bookingId);
      await loadBookings();
      onCancel(bookingId);
    } catch (error) {
      console.error('Ошибка удаления бронирования:', error);
      setError('Не удалось отменить бронь. Попробуйте позже.');
      setTimeout(() => setError(null), 5000);
    }
  };

  // ======================= СОСТОЯНИЕ ЗАГРУЗКИ =======================
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Загрузка данных...</div>
        <BottomMenu />
      </div>
    );
  }

  // ======================= ОСНОВНОЙ РЕНДЕРИНГ =======================
  return (
    <div className={styles.container}>
      <h1 className={styles.title}>КОРЗИНА</h1>

      {/* Блок ошибок */}
      {error && <div className={styles.error}>{error}</div>}

      {/* Блок с ID и кнопкой копирования */}
      {currentBookings.filter(b => !b.is_confirmed).length > 0 && (
        <div className={styles.paymentNotice}>
          <div className={styles.noticeText}>
            При оплате скопируйте ваш ID телеграмма и вставьте в сообщение к переводу
          </div>
          <div className={styles.userIdRow}>
            <span className={styles.userId}>
              ID: {bookings[0]?.client_telegram_id || 'N/A'}
            </span>
            <button
              className={styles.copyButton}
              onClick={() => {
                navigator.clipboard.writeText(String(bookings[0]?.client_telegram_id));
                setShowCopiedMessage(true);
                setTimeout(() => setShowCopiedMessage(false), 2000);
              }}
            >
              Копировать
            </button>
          </div>
          {showCopiedMessage && (
            <div className={styles.copySuccess}>ID скопирован</div>
          )}
        </div>
      )}

      {/* Кнопка оплаты */}
      {totalPendingSum > 0 && (
        <div className={styles.paymentButtonContainer}>
          <a
            className={styles.payButton}
            href={payUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Оплатить {totalPendingSum
              .toLocaleString('ru-RU', {
                style: 'decimal',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
                useGrouping: true
              })
              .replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
            ₽
          </a>
        </div>
      )}

      {/* Список текущих бронирований */}
      <div className={styles.bookingsList}>
        {currentBookings.map(booking => (
          <div key={booking.booking_id} className={styles.bookingItem}>
            <div className={styles.bookingHeader}>
              <span className={styles.serviceName}>
                {booking.service?.name || 'Неизвестная услуга'}
              </span>
              {booking.is_confirmed ? (
                <span className={styles.confirmed}>Подтверждено</span>
              ) : (
                <span className={styles.pending}>Не подтверждено</span>
              )}
            </div>

            <div className={styles.bookingInfo}>
              <div className={styles.infoRow}>
                <span className={styles.label}>Категория:</span>
                <span className={styles.value}>{booking.category?.name || '-'}</span>
              </div>

              <div className={styles.infoRow}>
                <span className={styles.label}>
                  {booking.service?.person_based_pricing ? 'Описание' : 'Продолжительность'}
                </span>
                <span className={styles.value}>
                  {booking.service?.person_based_pricing
                    ? booking.service?.description || 'Не указано'
                    : `${calculateDurationInHours(booking.start_time, booking.end_time)} ч`}
                </span>
              </div>

              {booking.service && (
                <div className={styles.infoRow}>
                  <span className={styles.label}>Количество:</span>
                  <span className={styles.value}>{booking.quantity || 1} шт.</span>
                </div>
              )}

              <div className={styles.infoRow}>
                <span className={styles.label}>Дата:</span>
                <span className={styles.value}>
                  {new Date(booking.booking_date).toLocaleDateString('ru-RU', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </div>

              {!booking.service?.person_based_pricing && (
                <div className={styles.infoRow}>
                  <span className={styles.label}>Время:</span>
                  <span className={styles.value}>
                    {booking.start_time?.split('T')[1]?.slice(0, 5) || '--:--'} -
                    {booking.end_time?.split('T')[1]?.slice(0, 5) || '--:--'}
                  </span>
                </div>
              )}

              <div className={styles.infoRow}>
                <span className={styles.label}>Стоимость:</span>
                <span className={styles.value}>
                  {booking.totalPrice?.toLocaleString('ru-RU')} ₽
                </span>
              </div>
            </div>
            {booking.is_confirmed && (<div className={styles.actionsContainer}>
              <a
                className={styles.adminButton}
                href={adminUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Написать администратору
              </a>
            </div>)}
            {!booking.is_confirmed && (
              <div className={styles.actionsContainer}>
                <button
                  className={styles.cancelButton}
                  onClick={() => handleCancel(booking.booking_id)}
                >
                  Отменить бронь
                </button>
              </div>
            )}
          </div>
        ))}

        {/* История бронирований */}
        {historyBookings.length > 0 && (
          <div className={styles.bookingItem}>
            <button
              className={styles.showHistoryButton}
              onClick={() => setShowHistory(!showHistory)}
            >
              {showHistory ? 'Скрыть историю' : 'Показать историю бронирований'}
            </button>

            {showHistory && historyBookings.map(booking => (
              <div key={booking.booking_id} className={styles.bookingItem}>
                <div className={styles.bookingHeader}>
                  <span className={styles.serviceName}>
                    {booking.service?.name || 'Неизвестная услуга'}
                  </span>
                  <span className={styles.confirmed}>Подтверждено</span>
                </div>

                <div className={styles.bookingInfo}>
                  <div className={styles.infoRow}>
                    <span className={styles.label}>Категория:</span>
                    <span className={styles.value}>{booking.category?.name || '-'}</span>
                  </div>

                  <div className={styles.infoRow}>
                    <span className={styles.label}>
                      {booking.service?.person_based_pricing ? 'Описание' : 'Продолжительность'}
                    </span>
                    <span className={styles.value}>
                      {booking.service?.person_based_pricing
                        ? booking.service?.description || 'Не указано'
                        : `${calculateDurationInHours(booking.start_time, booking.end_time)} ч`}
                    </span>
                  </div>

                  {booking.service && (
                    <div className={styles.infoRow}>
                      <span className={styles.label}>Количество:</span>
                      <span className={styles.value}>{booking.quantity || 1} шт.</span>
                    </div>
                  )}

                  <div className={styles.infoRow}>
                    <span className={styles.label}>Дата:</span>
                    <span className={styles.value}>
                      {new Date(booking.booking_date).toLocaleDateString('ru-RU', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </span>
                  </div>

                  {!booking.service?.person_based_pricing && (
                    <div className={styles.infoRow}>
                      <span className={styles.label}>Время:</span>
                      <span className={styles.value}>
                        {booking.start_time?.split('T')[1]?.slice(0, 5) || '--:--'} -
                        {booking.end_time?.split('T')[1]?.slice(0, 5) || '--:--'}
                      </span>
                    </div>
                  )}

                  <div className={styles.infoRow}>
                    <span className={styles.label}>Стоимость:</span>
                    <span className={styles.value}>
                      {booking.totalPrice?.toLocaleString('ru-RU')} ₽
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Пустая корзина */}
        {bookings.length === 0 && (
          <div className={styles.emptyCart}>
            <span className={styles.emptyText}>Ваша корзина пуста</span>
            <span className={styles.emptySubtext}>Добавьте услуги через каталог</span>
          </div>
        )}
      </div>

      <BottomMenu />
    </div>
  );
};